/**
 * A VERIFICAÇÃO DE UM LANÇAMENTO — e a conferência dele (0122).
 *
 * ## O que faltava
 *
 * O sistema já sabia dizer que um consumo ficou fora da faixa: desde a 0011 o
 * desvio é apurado no servidor e a lista o mostra como pendência. O que não
 * existia era o outro lado — alguém olhou e disse que está certo. Sem isso o
 * desvio fica aceso para sempre, e quem confere com o papel na mão (a máquina
 * trabalhou em areia, o consumo subiu mesmo) não tem onde riscar o item.
 *
 * Indicador que ninguém consegue zerar é indicador que todo mundo aprende a
 * ignorar.
 *
 * ## Por que isto é um módulo, e não lógica dentro da tela
 *
 * São três respostas que a tela dá em três lugares diferentes — o selo da
 * linha, a lista de verificações do painel e o número do indicador "Para
 * conferir" — e as três têm de concordar. Com a regra escrita dentro do
 * componente, o selo diria "Conferir consumo" numa linha que o indicador não
 * contou, e a pessoa passaria a tarde procurando a quarta.
 *
 * ## O QUE NÃO ESTÁ AQUI, e por quê
 *
 * Este arquivo julga o lançamento **depois** de ele existir. As verificações
 * de ANTES de lançar — cabe no tanque da máquina, há saldo na origem — moram
 * em `lancamento-guiado.ts`, que é quem o formulário e as cinco perguntas
 * consultam enquanto a pessoa digita. Aqui a pergunta é outra: o que já está
 * no livro está em ordem?
 *
 * CORREÇÃO (25/10/2026): a primeira versão deste arquivo afirmava, aqui e no
 * commit que o trouxe, que "o sistema não sabe a capacidade do tanque da
 * máquina". **Sabe.** `ativos.equipamento.capacidade_tanque_l` existe desde a
 * migração 0006, é herdada da configuração pela view, já vinha na API do
 * combustível e morria no mapeamento do domínio. A afirmação era falsa, e
 * estava escrita em código e em documento — que é a falha com cicatriz neste
 * projeto. O dado agora chega, e a verificação existe.
 */
import {
  PENDENCIA_CONSUMO_FORA_DA_FAIXA,
  PENDENCIA_SEM_CENTRO_DE_CUSTO,
  PENDENCIA_SEM_OPERADOR,
  textoDaFaixa,
  type Lancamento,
} from './consumo';
import type { FaixaConsumo } from './tipos';

/**
 * O tom de uma verificação.
 *
 * `pendente` e `naoSeAplica` NÃO são a mesma coisa, e nenhuma das duas é `ok`:
 * "não sei" e "não vale para este caso" são respostas diferentes de "está
 * certo", e desenhá-las iguais é como um sistema passa a afirmar o que não
 * sabe. É a mesma distinção que o `foraDaFaixa` do servidor faz entre `false`
 * e `null`.
 */
export type NivelDaVerificacao = 'ok' | 'atencao' | 'erro' | 'pendente' | 'naoSeAplica';

export interface Verificacao {
  readonly nivel: NivelDaVerificacao;
  /** O fato, em até quatro palavras: é o que se lê correndo o olho. */
  readonly titulo: string;
  /** Os números por trás dele, e o que fazer quando há algo a fazer. */
  readonly detalhe: string;
}

/**
 * O estado de verificação de UMA linha, que o selo da tabela mostra.
 *
 * Três estados, e a ordem de precedência importa: quem já foi conferido está
 * conferido mesmo que o consumo tenha ficado fora da faixa — é exatamente esse
 * o caso que a conferência existe para resolver.
 */
export type EstadoDaVerificacao = 'conferido' | 'conferir' | 'verificado';

/** A grafia de cada estado na tela. Um nome só para cada coisa. */
export const ROTULO_DA_VERIFICACAO: Readonly<Record<EstadoDaVerificacao, string>> = {
  conferido: 'Conferido',
  conferir: 'Conferir consumo',
  verificado: 'Verificado',
};

export function estadoDaVerificacao(lancamento: Lancamento): EstadoDaVerificacao {
  if (lancamento.abastecimento.conferencia) return 'conferido';
  /* A MESMA fonte da pendência, e não um segundo cálculo: `pendenciasDe` já
     decide isso com o veredito do servidor e com a queda local, e já sabe que
     `null` não vira aviso. Reimplementar a decisão aqui produziria uma tela
     que marca "Conferir consumo" numa linha sem pendência nenhuma. */
  if (lancamento.pendencias.includes(PENDENCIA_CONSUMO_FORA_DA_FAIXA)) return 'conferir';
  return 'verificado';
}

/**
 * Quantos lançamentos esperam conferência.
 *
 * É o número do indicador "Para conferir". Conta só o que PRECISA ser olhado —
 * consumo fora da faixa ainda sem assinatura: um indicador que contasse todas
 * as linhas não conferidas marcaria a frota inteira todos os dias, e ninguém
 * olharia nenhuma.
 */
export function paraConferir(lancamentos: readonly Lancamento[]): number {
  return lancamentos.filter((l) => estadoDaVerificacao(l) === 'conferir').length;
}

/**
 * A faixa da máquina julga ESTE lançamento?
 *
 * Não julga o ARLA nem o lubrificante: a faixa é de litro de COMBUSTÍVEL por
 * hora, e comparar o ARLA com ela acusaria desvio em toda máquina que usa ARLA.
 * É a mesma razão pela qual o servidor devolve `null` em vez de `false` no
 * `fora_da_faixa` fora da categoria.
 *
 * Quem pergunta é a tela, antes de DESENHAR a barra: a lista de verificações
 * dizia "não se aplica ao ARLA 32" e a barra, logo acima, desenhava a faixa do
 * diesel com a marca do ARLA fora dela. Duas respostas para a mesma pergunta,
 * a 20 px de distância.
 *
 * Categoria AUSENTE conta como combustível: é a linha que não passou pelas
 * views do módulo 08 (massa de teste, fila do celular), e nela a faixa é a
 * única referência que existe.
 */
export function faixaJulgaOConsumo(lancamento: Lancamento): boolean {
  const categoria = lancamento.abastecimento.produtoCategoria;
  return categoria === undefined || categoria === 'combustivel';
}

/**
 * A posição do consumo dentro da faixa, em porcentagem da barra.
 *
 * A barra vai de metade do piso a uma vez e meia o teto — e não da faixa
 * exata: com a barra começando no piso, todo consumo abaixo dele desenha a
 * marca encostada na borda esquerda, e "pouco abaixo" fica igual a "metade do
 * esperado". A folga de cada lado é o que dá ao desvio um tamanho visível.
 *
 * `null` quando não há barra honesta a desenhar: sem consumo, ou com a faixa
 * pela metade. O banco aceita a faixa com uma ponta só de propósito (o gestor
 * que só se importa com desperdício cadastra o teto), e uma barra com um lado
 * aberto afirmaria um limite inexistente do outro.
 */
export function posicaoNaFaixa(
  consumo: number | null,
  faixa: FaixaConsumo | null,
): { readonly inicio: number; readonly largura: number; readonly marca: number } | null {
  if (consumo === null || faixa === null) return null;
  const { minimo, maximo } = faixa;
  if (minimo === null || maximo === null || maximo <= minimo) return null;
  const inicioDaBarra = minimo * 0.5;
  const fimDaBarra = maximo * 1.5;
  const posicao = (valor: number) =>
    Math.max(0, Math.min(100, ((valor - inicioDaBarra) / (fimDaBarra - inicioDaBarra)) * 100));
  const inicio = posicao(minimo);
  return { inicio, largura: posicao(maximo) - inicio, marca: posicao(consumo) };
}

const UM_DECIMAL = (valor: number) => valor.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const ATE_UM_DECIMAL = (valor: number) => valor.toLocaleString('pt-BR', { maximumFractionDigits: 1 });

/**
 * As verificações de um lançamento JÁ LANÇADO, na ordem em que se leem.
 *
 * Medidor primeiro, porque é dele que sai o consumo: horímetro errado produz
 * consumo errado, e mostrar o consumo antes faria a pessoa procurar o problema
 * no volume. Depois o consumo contra a faixa. Por último o que falta de
 * cadastro no lançamento — operador e centro de custo não mudam número nenhum,
 * mas são o que impede o custo de chegar à obra certa.
 */
export function verificacoesDoLancamento(lancamento: Lancamento): readonly Verificacao[] {
  const { abastecimento, anterior, horas, consumo, unidade, faixa } = lancamento;
  const medida = unidade === 'L/km' ? 'km' : 'h';
  const lista: Verificacao[] = [];

  /* ------------------------------------------------------------------ medidor */
  if (abastecimento.horimetro === null) {
    lista.push({
      nivel: 'pendente',
      titulo: 'Sem leitura do medidor',
      detalhe: `O lançamento não trouxe leitura, e sem ela não há consumo. Registre a leitura pelo apontamento da máquina para o intervalo voltar a fechar.`,
    });
  } else if (anterior?.horimetro != null && abastecimento.horimetro < anterior.horimetro) {
    lista.push({
      nivel: 'erro',
      titulo: 'Medidor menor que a leitura anterior',
      detalhe: `${ATE_UM_DECIMAL(abastecimento.horimetro)} ${medida} é menor que ${ATE_UM_DECIMAL(anterior.horimetro)} ${medida}, do abastecimento anterior. Corrija o lançamento com a leitura certa.`,
    });
  } else if (horas === null) {
    lista.push({
      nivel: 'pendente',
      titulo: 'Sem intervalo para comparar',
      detalhe: `Leitura de ${ATE_UM_DECIMAL(abastecimento.horimetro)} ${medida}. O consumo aparece no próximo abastecimento desta máquina, que fecha o intervalo.`,
    });
  } else {
    lista.push({
      nivel: 'ok',
      titulo: 'Medidor em ordem',
      detalhe: `Andou ${ATE_UM_DECIMAL(horas)} ${medida} desde a leitura anterior, até ${ATE_UM_DECIMAL(abastecimento.horimetro)} ${medida}.`,
    });
  }

  /* ------------------------------------------------------------------ consumo */
  const foraDaFaixa = lancamento.pendencias.includes(PENDENCIA_CONSUMO_FORA_DA_FAIXA);
  const escritaDaFaixa = faixa === null ? null : textoDaFaixa(faixa.minimo, faixa.maximo, unidade);
  if (!faixaJulgaOConsumo(lancamento)) {
    /* ARLA e lubrificante NÃO têm consumo por hora, e julgá-los pela faixa do
       diesel acusaria desvio em toda máquina que usa ARLA — é a mesma razão
       pela qual o servidor devolve `null` em vez de `false`. */
    lista.push({
      nivel: 'naoSeAplica',
      titulo: `Consumo por ${medida === 'km' ? 'quilômetro' : 'hora'} não se aplica`,
      detalhe: `A faixa é de litro de combustível por ${medida}. ${abastecimento.produto ?? 'Este produto'} tem a cadeia própria, e não entra nela.`,
    });
  } else if (consumo === null) {
    lista.push({
      nivel: 'pendente',
      titulo: `Consumo por ${medida === 'km' ? 'quilômetro' : 'hora'} ainda não apurado`,
      detalhe: 'Ele sai de duas leituras do mesmo produto na mesma máquina. Com uma só, o intervalo ainda está aberto.',
    });
  } else if (escritaDaFaixa === null) {
    lista.push({
      nivel: 'naoSeAplica',
      titulo: `Consumo de ${UM_DECIMAL(consumo)} ${unidade}`,
      detalhe: 'Sem faixa cadastrada para esta máquina, o consumo é apurado e mostrado, mas o sistema não tem contra o que compará-lo. A faixa entra no cadastro do equipamento.',
    });
  } else if (foraDaFaixa) {
    lista.push({
      nivel: 'atencao',
      titulo: `Consumo fora da faixa: ${UM_DECIMAL(consumo)} ${unidade}`,
      detalhe: `A faixa da máquina é de ${escritaDaFaixa}. Confira o medidor e o volume com quem abasteceu; se os dois estiverem certos, marque o lançamento como conferido.`,
    });
  } else {
    lista.push({
      nivel: 'ok',
      titulo: `Consumo dentro da faixa: ${UM_DECIMAL(consumo)} ${unidade}`,
      detalhe: `A faixa da máquina é de ${escritaDaFaixa}.`,
    });
  }

  /* ------------------------------------------------- o que falta no lançamento */
  const faltando = lancamento.pendencias.filter(
    (p) => p === PENDENCIA_SEM_OPERADOR || p === PENDENCIA_SEM_CENTRO_DE_CUSTO,
  );
  if (faltando.length > 0) {
    lista.push({
      nivel: 'pendente',
      titulo: faltando.join(' · '),
      detalhe: faltando.includes(PENDENCIA_SEM_CENTRO_DE_CUSTO)
        ? 'Sem centro de custo o litro não chega ao custo da obra. Corrija o lançamento para informá-lo.'
        : 'Sem operador não há a quem perguntar sobre a leitura. Corrija o lançamento para informá-lo.',
    });
  }

  /* ------------------------------------------------------------- a conferência */
  const conferencia = abastecimento.conferencia;
  if (conferencia) {
    lista.push({
      nivel: 'ok',
      titulo: 'Conferido',
      detalhe: conferencia.por === null ? 'Conferido; quem conferiu não está mais cadastrado.' : `Conferido por ${conferencia.por}.`,
    });
  }

  return lista;
}

/**
 * Quem lançou e de onde, numa frase — "Às 07:21 por Jorge Nunes · celular".
 *
 * O aparelho entra porque muda o que se confere: o número digitado em pé, na
 * frente de serviço, tem outro risco de digitação do que o conferido com o
 * cupom na mão. Sem responsável conhecido a frase não inventa um: ela diz a
 * hora e o aparelho, que é o que se sabe.
 */
export function origemDaLinha(lancamento: Lancamento, hora: string): string {
  const { abastecimento } = lancamento;
  const quem = abastecimento.responsavel ?? abastecimento.operador;
  const aparelho = abastecimento.doCelular === true ? 'celular' : 'mesa';
  const onde = abastecimento.doCelular === true ? 'pelo celular' : 'pela mesa';
  return quem === null ? `Às ${hora}, ${onde}` : `Às ${hora} por ${quem} · ${aparelho}`;
}
